import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ArrowRight, MapPin, Waves } from "lucide-react";
import InquiryForm from "@/components/landing/InquiryForm";
import { siteContent } from "@/data/siteContent";

const scrollToSection = (sectionId) => {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const buttonBaseClass =
  "inline-flex h-12 items-center justify-center rounded-sm px-8 text-sm tracking-[0.18em] uppercase transition duration-300";

export default function LandingPage() {
  const marqueeItems = useMemo(() => [...siteContent.coastlineAreas, ...siteContent.coastlineAreas], []);

  return (
    <main className="overflow-x-hidden bg-background text-foreground" data-testid="suncoast-landing-page">
      <header className="fixed inset-x-0 top-0 z-50 px-4 py-4 md:px-8" data-testid="site-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between border border-white/30 bg-white/70 px-4 py-3 shadow-[0_12px_40px_rgba(15,31,56,0.08)] backdrop-blur-md md:px-6">
          <a className="flex items-center gap-3" data-testid="nav-home-link" href="#top">
            <div className="h-9 w-[150px] overflow-hidden md:h-10 md:w-[190px]" data-testid="brand-logo-wrapper">
              <img
                alt={siteContent.logo.alt}
                className="h-full w-full scale-[1.65] object-contain object-center"
                data-testid="brand-logo-image"
                src={siteContent.logo.url}
              />
            </div>
          </a>

          <nav className="hidden items-center gap-6 md:flex" data-testid="primary-navigation">
            {[
              ["About", "about"],
              ["Gallery", "gallery"],
              ["Service Area", "service-area"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <button
                className="text-xs font-medium uppercase tracking-[0.2em] text-primary transition-colors hover:text-[#1C3359]"
                data-testid={`nav-${id}-button`}
                key={id}
                onClick={() => scrollToSection(id)}
                type="button"
              >
                {label}
              </button>
            ))}
            <Link className="text-xs font-medium uppercase tracking-[0.2em] text-primary transition-colors hover:text-[#1C3359]" data-testid="nav-investor-link" to="/investor">
              Investor
            </Link>
          </nav>

          <a
            className="hidden items-center rounded-sm bg-primary px-5 py-3 text-sm text-primary-foreground transition duration-300 hover:bg-[#1C3359] md:inline-flex"
            data-testid="nav-contact-cta-button"
            href={siteContent.contact.phoneHref}
          >
            Call Suncoast Homes
          </a>
        </div>
      </header>

      <section
        className="relative flex min-h-[90vh] items-end pt-28"
        data-testid="hero-section"
        id="top"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(${siteContent.heroImage})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-6 pb-12 pt-24 md:px-12 lg:grid-cols-[minmax(0,1.4fr)_340px] lg:px-24 lg:pb-20">
          <div className="max-w-3xl text-white">
            <p className="fade-up text-xs uppercase tracking-[0.28em] text-white/80" data-testid="hero-overline">
              {siteContent.hero.overline}
            </p>
            <h1 className="fade-up-delay-1 mt-6 max-w-4xl font-serif text-5xl font-light tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
              {siteContent.hero.title}
            </h1>
            <p className="fade-up-delay-2 mt-6 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg" data-testid="hero-subtitle">
              {siteContent.hero.subtitle}
            </p>

            <div className="fade-up-delay-3 mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                className={`${buttonBaseClass} bg-white text-primary hover:bg-[#f0f0eb]`}
                data-testid="hero-primary-cta-button"
                href={siteContent.contact.emailHref}
              >
                Email Suncoast Homes
              </a>
              <button
                className={`${buttonBaseClass} border border-white/60 bg-transparent text-white hover:bg-white/10`}
                data-testid="hero-secondary-cta-button"
                onClick={() => scrollToSection("gallery")}
                type="button"
              >
                Explore featured homes
              </button>
            </div>
          </div>

          <div className="fade-up-delay-4 self-end border border-white/20 bg-white/10 p-6 text-white backdrop-blur-sm" data-testid="hero-summary-card">
            <p className="text-xs uppercase tracking-[0.24em] text-white/70" data-testid="hero-summary-overline">
              Suncoast Homes
            </p>
            <div className="mt-6 space-y-5">
              {siteContent.stats.map((item) => (
                <div className="border-b border-white/15 pb-4 last:border-b-0 last:pb-0" data-testid={`hero-stat-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.label}>
                  <p className="font-serif text-3xl font-light" data-testid={`hero-stat-value-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {item.value}
                  </p>
                  <p className="mt-1 text-sm uppercase tracking-[0.18em] text-white/70" data-testid={`hero-stat-label-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-border/80 bg-[#f7f6f1]" data-testid="turnkey-strip-section">
        <div className="mx-auto max-w-7xl px-6 py-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-center gap-3" data-testid="turnkey-strip-items">
            {siteContent.turnkeyHighlights.map((item) => (
              <span className="border border-border bg-white px-4 py-3 text-xs uppercase tracking-[0.2em] text-primary/80" data-testid={`turnkey-highlight-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item}>
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-border/80 bg-[#f0f0eb] py-5" data-testid="service-ribbon-section">
        <div className="marquee-track" data-testid="service-ribbon-track">
          {marqueeItems.map((area, index) => (
            <span className="marquee-item font-serif text-2xl font-light text-[#94A69B]" data-testid={`service-ribbon-item-${index}`} key={`${area}-${index}`}>
              {area}
            </span>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 lg:px-24 lg:py-32" data-testid="process-section" id="process">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-testid="process-layout">
          <div data-testid="process-copy-column">
            <p className="text-xs uppercase tracking-[0.24em] text-[#94A69B]" data-testid="process-overline">
              Custom-build process
            </p>
            <h2 className="mt-5 font-serif text-4xl font-light tracking-tight text-primary sm:text-5xl" data-testid="process-title">
              A clear turnkey path.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="process-description">
              Your dedicated project coordinator guides each stage, with specialists leading the work.
            </p>
          </div>

          <div className="grid gap-4" data-testid="process-cards-grid">
            {siteContent.processSteps.map((step) => (
              <article className="grid gap-5 border border-border bg-white p-6 md:grid-cols-[88px_1fr] md:items-start" data-testid={`process-card-${step.phase}`} key={step.phase}>
                <div className="border-b border-border pb-3 md:border-b-0 md:border-r md:pb-0 md:pr-5" data-testid={`process-phase-block-${step.phase}`}>
                  <p className="font-serif text-4xl font-light text-primary" data-testid={`process-phase-number-${step.phase}`}>
                    {step.phase}
                  </p>
                </div>
                <div data-testid={`process-step-content-${step.phase}`}>
                  <p className="font-serif text-2xl text-primary" data-testid={`process-step-title-${step.phase}`}>
                    {step.title}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base" data-testid={`process-step-description-${step.phase}`}>
                    {step.description}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid={`process-step-details-${step.phase}`}>
                    {step.details.map((detail) => (
                      <span className="border border-border bg-[#f7f6f1] px-3 py-2 text-xs uppercase tracking-[0.18em] text-primary/75" data-testid={`process-detail-${step.phase}-${detail.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={detail}>
                        {detail}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 lg:px-24 lg:py-32" data-testid="gallery-section" id="gallery">
        <div className="flex max-w-3xl flex-col gap-4" data-testid="gallery-header">
          <p className="text-xs uppercase tracking-[0.24em] text-[#94A69B]" data-testid="gallery-overline">
            Renderings & floor plan concepts
          </p>
          <h2 className="font-serif text-4xl font-light tracking-tight text-primary sm:text-5xl" data-testid="gallery-title">
            Renderings and floor plans for the next coastal build.
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="gallery-description">
            Brochure renderings and floor plan references for planning and investor conversations.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 md:auto-rows-[220px]" data-testid="gallery-bento-grid">
          {siteContent.featuredProjects.map((project) => (
            <article className={`group relative overflow-hidden ${project.span}`} data-testid={`gallery-card-${project.name.toLowerCase().replace(/\s+/g, "-")}`} key={project.name}>
              <img
                alt={project.name}
                className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-105"
                data-testid={`gallery-image-${project.name.toLowerCase().replace(/\s+/g, "-")}`}
                src={project.image}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f1f38]/80 via-[#0f1f38]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white" data-testid={`gallery-overlay-${project.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-white/70" data-testid={`gallery-location-${project.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  {project.location}
                </p>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <p className="font-serif text-2xl font-light" data-testid={`gallery-name-${project.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {project.name}
                  </p>
                  <ArrowRight className="h-5 w-5 text-white/80" data-testid={`gallery-arrow-${project.name.toLowerCase().replace(/\s+/g, "-")}`} />
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-primary text-white" data-testid="service-area-section" id="service-area">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 lg:px-24 lg:py-28">
          <div data-testid="service-area-copy-column">
            <p className="text-xs uppercase tracking-[0.24em] text-white/60" data-testid="service-area-overline">
              Beach markets we know well
            </p>
            <h2 className="mt-5 font-serif text-4xl font-light tracking-tight text-white sm:text-5xl" data-testid="service-area-title">
              From Clearwater Beach south to Boca Ciega Bay, each community calls for its own thoughtful approach.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 md:text-lg" data-testid="service-area-description">
              Some areas skew hospitality and rentals. Others mix legacy cottages with high-end luxury replacements. We shape the approach around the lot, market, and buyer.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[1.15fr_0.85fr]" data-testid="service-area-layout-grid">
            <div className="grid gap-5 md:grid-cols-2" data-testid="service-area-neighborhood-grid">
              {siteContent.coastalNeighborhoods.map((market) => (
                <article className="border border-white/15 bg-white/5 p-5 backdrop-blur-sm" data-testid={`service-area-card-${market.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={market.title}>
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#D9D1C7]" data-testid={`service-area-icon-${market.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                    <div>
                      <p className="font-serif text-2xl font-light text-white" data-testid={`service-area-name-${market.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {market.title}
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-white/70" data-testid={`service-area-note-${market.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {market.summary}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="grid gap-4" data-testid="market-services-grid">
              {siteContent.marketServices.map((service) => (
                <div className="border border-white/15 bg-white/8 p-6 backdrop-blur-sm" data-testid={`market-service-card-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={service.title}>
                  <p className="font-serif text-3xl font-light text-white" data-testid={`market-service-title-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {service.title}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-white/72" data-testid={`market-service-description-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 border-t border-white/15 pt-8" data-testid="coastline-coverage-block">
            <p className="text-xs uppercase tracking-[0.24em] text-white/60" data-testid="coastline-coverage-overline">
              Coastline covered
            </p>
            <div className="mt-4 flex flex-wrap gap-3" data-testid="coastline-coverage-tags">
              {siteContent.coastlineAreas.map((area) => (
                <span className="border border-white/15 bg-white/6 px-3 py-2 text-xs uppercase tracking-[0.18em] text-white/75" data-testid={`coastline-tag-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={area}>
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 lg:px-24 lg:py-28" data-testid="investor-teaser-section">
        <div className="grid gap-10 border border-border bg-[#f7f6f1] p-8 md:p-10 lg:grid-cols-[1fr_auto] lg:items-end lg:p-14" data-testid="investor-teaser-panel">
          <div data-testid="investor-teaser-copy-column">
            <p className="text-xs uppercase tracking-[0.24em] text-[#94A69B]" data-testid="investor-teaser-overline">
              Investor page
            </p>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl font-light tracking-tight text-primary sm:text-5xl" data-testid="investor-teaser-title">
              {siteContent.investorTeaser.title}
            </h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="investor-teaser-description">
              {siteContent.investorTeaser.description}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col" data-testid="investor-teaser-actions">
            <Link className="inline-flex h-12 items-center justify-center rounded-sm bg-primary px-8 text-sm uppercase tracking-[0.18em] text-primary-foreground transition duration-300 hover:bg-[#1C3359]" data-testid="investor-teaser-primary-link" to="/investor">
              View investor page
            </Link>
            <button className="inline-flex h-12 items-center justify-center rounded-sm border border-primary/20 px-8 text-sm uppercase tracking-[0.18em] text-primary transition duration-300 hover:bg-primary/5" data-testid="investor-teaser-secondary-button" onClick={() => scrollToSection("contact")} type="button">
              Start a conversation
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:px-12 lg:grid-cols-2 lg:px-24 lg:py-32" data-testid="about-section" id="about">
        <div className="flex flex-col justify-center order-2 lg:order-1" data-testid="about-copy-column">
          <p className="text-xs uppercase tracking-[0.24em] text-[#94A69B]" data-testid="about-overline">
            Third-generation Tampa Bay builder
          </p>
          <h2 className="mt-5 max-w-xl font-serif text-4xl font-light tracking-tight text-primary sm:text-5xl" data-testid="about-title">
            Legacy, backed by a modern turnkey process.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="about-description-primary">
            Suncoast Homes blends heritage craftsmanship with in-house design, engineering, and construction.
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="about-description-secondary">
            Your plan or ours. Your lot or ours. One team guiding the full path.
          </p>

          <div className="mt-10 grid gap-4" data-testid="about-pillars-grid">
            {siteContent.pillars.map((pillar) => (
              <div className="border border-border bg-white p-5" data-testid={`pillar-card-${pillar.title.toLowerCase().replace(/\s+/g, "-")}`} key={pillar.title}>
                <p className="font-serif text-2xl text-primary" data-testid={`pillar-title-${pillar.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  {pillar.title}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground" data-testid={`pillar-description-${pillar.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden order-1 lg:order-2" data-testid="about-image-wrapper">
          <img
            alt="The Frierson heritage home"
            className="h-full min-h-[520px] w-full object-cover object-center"
            data-testid="about-legacy-image"
            src={siteContent.legacyImage}
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0f1f38]/75 to-transparent p-6 text-white" data-testid="about-image-caption">
            <p className="text-xs uppercase tracking-[0.22em] text-white/70" data-testid="about-image-caption-label">
              Legacy home
            </p>
            <p className="mt-2 font-serif text-2xl" data-testid="about-image-caption-title">
              Check out some of our original 1918 builds still standing.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 lg:px-24 lg:py-32" data-testid="contact-section" id="contact">
        <div className="grid gap-12 border border-border bg-[#f7f6f1] p-8 md:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:p-14" data-testid="contact-panel">
          <div data-testid="contact-copy-column">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#94A69B]" data-testid="contact-overline">
              <Waves className="h-4 w-4" />
              Private inquiry
            </div>
            <h2 className="mt-5 max-w-md font-serif text-4xl font-light tracking-tight text-primary sm:text-5xl" data-testid="contact-title">
              Speak with a builder about your project.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="contact-description">
              Tell us about the lot, timing, and goals. We’ll guide the next step.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="contact-direct-actions">
              <a
                className={`${buttonBaseClass} bg-primary text-primary-foreground hover:bg-[#1C3359]`}
                data-testid="contact-call-direct-button"
                href={siteContent.contact.phoneHref}
              >
                Call directly
              </a>
              <a
                className={`${buttonBaseClass} border border-primary/20 bg-transparent text-primary hover:bg-primary/5`}
                data-testid="contact-email-direct-button"
                href={siteContent.contact.emailHref}
              >
                Email directly
              </a>
            </div>

            <div className="mt-10 space-y-4" data-testid="contact-notes-list">
              {[
                "Turnkey custom homes and full-home transformations",
                "Beach-market knowledge with pricing integrity",
                "Clear scope, fewer surprises, enduring craftsmanship",
              ].map((note, index) => (
                <div className="border-l border-primary/20 pl-4" data-testid={`contact-note-${index + 1}`} key={note}>
                  <p className="text-sm uppercase tracking-[0.18em] text-primary/70" data-testid={`contact-note-label-${index + 1}`}>
                    Suncoast standard
                  </p>
                  <p className="mt-1 text-base leading-relaxed text-primary" data-testid={`contact-note-text-${index + 1}`}>
                    {note}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div data-testid="contact-form-column">
            <div className="mb-6 border border-border bg-white p-5" data-testid="contact-testimonial-card">
              <p className="text-xs uppercase tracking-[0.22em] text-[#94A69B]" data-testid="contact-testimonial-label">
                Client words
              </p>
              <p className="mt-3 font-serif text-2xl font-light leading-tight text-primary" data-testid="contact-testimonial-quote">
                “{siteContent.testimonial.quote}”
              </p>
              <p className="mt-3 text-sm uppercase tracking-[0.18em] text-muted-foreground" data-testid="contact-testimonial-attribution">
                {siteContent.testimonial.author} • {siteContent.testimonial.location} • {siteContent.testimonial.project}
              </p>
            </div>
            <InquiryForm />
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-white" data-testid="site-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-12 lg:px-24">
          <div data-testid="footer-brand-block">
            <div className="h-9 w-[170px] overflow-hidden" data-testid="footer-logo-wrapper">
              <img
                alt={siteContent.logo.alt}
                className="h-full w-full scale-[1.65] object-contain object-center"
                data-testid="footer-logo-image"
                src={siteContent.logo.url}
              />
            </div>
            <p className="mt-3 text-sm text-muted-foreground" data-testid="footer-tagline">
              Coastal homes for buyers who value legacy, restraint, and lasting quality.
            </p>
          </div>
          <button
            className="w-fit text-xs uppercase tracking-[0.24em] text-primary transition hover:text-[#1C3359]"
            data-testid="footer-back-to-top-button"
            onClick={() => scrollToSection("top")}
            type="button"
          >
            Back to top
          </button>
        </div>
      </footer>
    </main>
  );
}